import { DateTime } from 'luxon'
import Validations from './Validations'
import { MENSAGEM_VALIDACAO, RULES } from '@/constants'
import store from '@/store/store'

/**
 * Recebe um data no formato dd/mm/yyyy e retorna yyyy-mm-dd para enviar pra API
 *
 * @param string date - Data no formato 'dd/mm/yyyy'.
 * @return string - Data no formato 'yyyy-mm-dd'.
 */
function dateToEN(date: string) {
	return date.split('/').reverse().join('-')
}

/**
 *
 * @param date  - Data no formato 'yyyy-mm-dd'
 * @return string Retorna uma data com o formato dia mes 'dd/mm'
 */
export const showDataDiaMes = (date: string) => {
	let _date = new Date(date)
	let mes = _date.toLocaleString('pt-BR', { month: 'short' })
	let dia = _date.toLocaleString('pt-BR', { day: '2-digit' })
	return dia + '/' + mes
}

/**
 * Retorna um DateTime pela string (caso tenham novos formatos de string, é só adicionar)
 * @param strDate - Data em string
 * @return DateTime - retorna a data convertida em DateTime
 */
export const parseDateFromString = (strDate: string): DateTime => {
	let dataParsed = DateTime.fromISO(strDate)

	if (dataParsed.isValid) return dataParsed

	dataParsed = DateTime.fromFormat(strDate, 'dd/MM/yyyy')

	//Adicionar outros formatos caso exista (sempre verificando se o parse anterior é valido, se for, retorna ele)
	// if (dataParsed.isValid) return dataParsed
	// novo parse

	return dataParsed
}

/**
 * Recebe a data de nascimento e o prazo maximo em anos e calcula quantos meses sobram até o prazo maximo
 * @param prazoMaximoAnos - Prazo maximo aceito em anos
 * @param dataNascimento - Data de nascimento
 * @return number - retorna o prazo maximo em meses baseado no prazo maximo em anos e a data de nascimento
 */
export const getPrazoMaximoMeses = (dataNascimento: string, prazoMaximoAnos: number) => {
	if (!dataNascimento) return 0

	if (!prazoMaximoAnos) prazoMaximoAnos = 0

	let dataNascimentoParsed: DateTime = parseDateFromString(dataNascimento)

	let prazoMaximoMeses = prazoMaximoAnos * 12
	let mesesIdade = Math.abs(dataNascimentoParsed.diffNow(['months']).months)
	const result = Math.round(prazoMaximoMeses - mesesIdade)

	if (result < 0) return 0

	return result
}

/**
 * Recebe a lista de datas de nascimento e verifica qual das idades da lista é maior
 * @param dataNascimentoList - Lista de strings das datas de nascimento
 * @return number - retorna a maior idade em anos
 */
export const getMaiorIdade = (dataNascimentoList: Array<string>): number => {
	let dataNascimentoAtual: DateTime
	let maiorIdade: number = 0

	dataNascimentoList.forEach((dataNascimento) => {
		dataNascimentoAtual = parseDateFromString(dataNascimento)

		if (maiorIdade < Math.abs(dataNascimentoAtual.diffNow(['years', 'months', 'days']).years))
			maiorIdade = Math.abs(dataNascimentoAtual.diffNow(['years', 'months', 'days']).years)
	})

	return maiorIdade
}

/**
 * Recebe a lista de datas de nascimento e verifica qual das idades da lista é maior
 * @param dataNascimentoList - Lista de strings das datas de nascimento
 * @return DateTime - retorna a data de nascimento com a maior idade
 */
export const getMaiorDataNascimento = (dataNascimentoList: Array<string>): DateTime => {
	var data = dataNascimentoList
		.filter((c) => c != null)
		.map((c) => parseDateFromString(c))
		.sort((a, b) => Number(a?.toJSDate()) - Number(b?.toJSDate()))

	if (data.length == 0) return DateTime.now()

	return data[0]
}

/**
 * Recebe a lista de datas de nascimento e verifica qual das idades da lista é maior
 * @param dataNascimentoList - Lista de strings das datas de nascimento
 * @return number - retorna a maior idade em anos
 */
export const isOver18years = (dataNascimento: string): boolean => {
	if (!dataNascimento) return false

	let dataNascimentoParsed: DateTime = parseDateFromString(dataNascimento)

	let idade = dataNascimentoParsed.diffNow(['years']).years

	return idade <= -18
}

/**
 * Recebe a data de nascimente e verifica se é menor que o limite
 * @param dataNascimento - Data de nascimento
 * @param limitAge - Idade limite
 * @return boolean - retorna se a data é menor que o limite
 */
export const isUnderLimitAge = (dataNascimento: string, limitAge: number): boolean => {
	if (!dataNascimento) return false

	let dataNascimentoParsed: DateTime = parseDateFromString(dataNascimento)

	let idade = dataNascimentoParsed.diffNow(['years']).years

	return idade >= -limitAge
}

/**
 * Recebe a data minima e a data fornecida e verifica se a data fornecida é maior que a data minima permitida
 * @param dataFornecida - Data fornecida pelo usuario/sistema
 * @param dataMinima - Data minima permitida (padrao para o dia de hoje)
 * @return boolean - retorna se data fornecida é maior que a data minima permitida
 */
export const compareMinDate = (dataFornecida: string, dataMinima: string | null = null): boolean => {
	if (!dataFornecida) return false

	let dataMinimaParsed: DateTime = DateTime.local()
	let dataFornecidaParsed: DateTime = parseDateFromString(dataFornecida)

	if (dataMinima) dataMinimaParsed = parseDateFromString(dataMinima)

	return dataFornecidaParsed > dataMinimaParsed
}

/**
 * Recebe a data maxima e a data fornecida e verifica se a data fornecida é maior que a data maxima permitida
 * @param dataFornecida - Data fornecida pelo usuario/sistema
 * @param dataMaxima - Data maxima permitida (padrao para o dia de hoje)
 * @return boolean - retorna se data fornecida é maior que a data maxima permitida
 */
export const compareMaxDate = (dataFornecida: string, dataMaxima: string | null = null): boolean => {
	if (!dataFornecida) return false

	let dataMaximaParsed: DateTime = DateTime.local()
	let dataFornecidaParsed: DateTime = parseDateFromString(dataFornecida)

	if (dataMaxima) dataMaximaParsed = parseDateFromString(dataMaxima)

	return dataFornecidaParsed < dataMaximaParsed
}

/**
 * Convert from ISO to PT-BR date
 * @param value
 * @returns
 */
export const toShortDateString = (value: string) => {
	const date = new Date(value)
	return date.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})
}

/**
 * Convert date/time to notifications date/time
 * @param date - Datetime to convert
 * @returns Convert date/time to notifications date/time
 */
export const notificationDateTime = (date: string): string => {
	const userDate = new Date(date)
	const now = new Date()

	// if the date is today, return the time
	if (userDate.getDate() === now.getDate() && userDate.getMonth() === now.getMonth() && userDate.getFullYear() === now.getFullYear()) {
		var ampm = userDate.getHours() >= 12 ? 'PM' : 'AM'
		const time = `${userDate.getHours().toString().padStart(2, '0')}:${userDate.getMinutes().toString().padStart(2, '0')} ${ampm}`

		return time
	}
	// or return date em format 'Mai 08'
	else {
		const formattedDate = userDate.toLocaleString('pt-BR', { month: 'short', day: '2-digit' }).replace('.', '')
		const [day, month] = formattedDate.split(' de ')
		const finalDate = `${month?.charAt(0).toUpperCase()}${month?.slice(1)} ${day}`

		return finalDate
	}
}

export const getTomorrowDate = () => {
	return DateTime.local().plus({ days: 1 }).startOf('day')
}

export const getNextMonthLastDay = () => {
	return getTomorrowDate().plus({ months: 1 }).endOf('month')
}

export const getFirstDayOfMonth = (date: string) => {
	const data = parseDateFromString(date)
	const firstDay = data.startOf('month')

	return firstDay
}

export const getFirstDayTwoMonthsAgo = () => {
	const currentDate = DateTime.now()
	const dateTwoMonthsAgo = currentDate.minus({ months: 2 })
	const firstDayTwoMonthsAgo = dateTwoMonthsAgo.startOf('month')

	return firstDayTwoMonthsAgo
}

export const getFirstDayTwelveMonthsAgo = () => {
	const currentDate = DateTime.now()
	const dateTwelveMonthsAgo = currentDate.minus({ months: 12 })
	const firstDayTwelveMonthsAgo = dateTwelveMonthsAgo.startOf('month')

	return firstDayTwelveMonthsAgo
}

export const getSignDateRule = () => {
	return [
		(v: string) =>
			Validations.isDateGreaterThan(
				v,
				getNextMonthLastDay().plus({ days: 1 }).toFormat('dd/MM/yyyy'),
				MENSAGEM_VALIDACAO.MAX_DATE + getNextMonthLastDay().toFormat('dd/MM/yyyy')
			),
		(v: string) =>
			Validations.isDateLessThan(
				v,
				getMinDtFinalizacao().minus({ days: 1 }).toFormat('dd/MM/yyyy'),
				MENSAGEM_VALIDACAO.MIN_DATE + getMinDtFinalizacao().toFormat('dd/MM/yyyy')
			)
	]
}

export const getMinDtFinalizacao = () => {
	const { state } = store
	const userRules = state?.user?.rules

	let lastCompletedClosingDate = state.app?.lastCompletedClosingDate

	if (userRules && userRules.includes(RULES.ISOLVE_CLOSING_ALLOW_ADM_ROUTINES)) {
		lastCompletedClosingDate = getFirstDayTwelveMonthsAgo()
	}

	if (lastCompletedClosingDate) {
		return getFirstDayOfMonth(lastCompletedClosingDate.toISO())
	} else {
		return getFirstDayTwoMonthsAgo()
	}
}
